import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormattedNumberInput } from "components/common/FormattedNumberInput";
import { InputV3 } from "components/common/input/InputV3";
import { Checkbox } from "components/common/checkbox/Checkbox";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "components/common/accordion";
import { CheckboxColor } from "store/types/enum";
import { featureGroups, MAX_BUDGET_VALUE, MIN_BUDGET_VALUE } from "../CreateProjectSheet.constants";
import { getFeatureData, getFeatureKey } from "../CreateProjectSheet.utils";
import type { FormData } from "../CreateProjectSheet.types";
import type { FeaturesSectionProps } from "./FeaturesSection.types";
import {
  FINAL_DRAFT_BREAKDOWN_OFF_HELPER,
  FINAL_DRAFT_BREAKDOWN_ON_HELPER,
  FINAL_DRAFT_FREE_BREAKDOWN_HELPER,
} from "services/finalDraftTags";

const DEFAULT_OPEN_SECTIONS = ["budget", "schedule"];

const OTP_DRAFT_FEATURES_NOTICE =
  "Scene breakdown is free. Characters, coverage, budget, and schedule need a Studio license.";

export const FeaturesSection = ({
  otpDraftWithoutAvailableLicense = false,
  finalDraftTagCount = 0,
}: FeaturesSectionProps) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext<FormData>();

  const hasBudget = watch("features.has_budget");
  const hasSchedule = watch("features.has_schedule");
  const hasScriptBreakdown = watch("features.has_script_breakdown");

  useEffect(() => {
    if (otpDraftWithoutAvailableLicense) {
      return;
    }
    if (hasBudget || hasSchedule) {
      setValue("features.has_script_breakdown", true, { shouldValidate: true });
    }
  }, [hasBudget, hasSchedule, otpDraftWithoutAvailableLicense, setValue]);

  // A tagged Final Draft file opens Analysis too, so the Script breakdown choice is in view.
  // Sections the user opened or closed stay as they are.
  const [openSections, setOpenSections] = useState<string[]>(DEFAULT_OPEN_SECTIONS);
  const hasFinalDraftTags = finalDraftTagCount > 0;
  useEffect(() => {
    if (!hasFinalDraftTags) return;
    setOpenSections((open) => (open.includes("analysis") ? open : [...open, "analysis"]));
  }, [hasFinalDraftTags]);

  const scriptBreakdownMessage =
    errors.features &&
    typeof errors.features === "object" &&
    "has_script_breakdown" in errors.features
      ? errors.features.has_script_breakdown?.message
      : undefined;

  // Without a license nothing here can be changed, so one line replaces the options.
  // The free scene breakdown stays on: turning it off here would also hide it from Run AI later.
  if (otpDraftWithoutAvailableLicense) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-xs text-muted-foreground">{OTP_DRAFT_FEATURES_NOTICE}</p>
        {finalDraftTagCount > 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="final-draft-breakdown-helper">
            {FINAL_DRAFT_FREE_BREAKDOWN_HELPER}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-medium text-foreground">Features & Configuration</h3>
      <Accordion type="multiple" value={openSections} onValueChange={setOpenSections}>
        {featureGroups.map((group) => {
          const Icon = group.icon;

          return (
            <AccordionItem key={group.id} value={group.id}>
              <AccordionTrigger>
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-primary" />
                  <div className="text-left">
                    <h4 className="text-sm font-medium text-foreground">{group.title}</h4>
                    <p className="text-xs text-muted-foreground">{group.description}</p>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent>
                <div className="flex flex-col gap-4">
                  {group.features.map((featureName) => {
                    const featureData = getFeatureData(featureName);
                    const featureKey = getFeatureKey(featureName);

                    if (!featureData) return null;

                    return (
                      <div key={featureName} className="flex flex-col gap-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h5 className="text-sm font-medium text-foreground">
                              {featureData.name}
                            </h5>
                            <p className="text-xs text-muted-foreground mt-1">{featureData.text}</p>
                          </div>
                          <Controller
                            name={`features.${featureKey}`}
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                name={`features.${featureKey}`}
                                checked={Boolean(field.value)}
                                disabled={otpDraftWithoutAvailableLicense}
                                onChange={() => {
                                  if (otpDraftWithoutAvailableLicense) {
                                    return;
                                  }
                                  if (featureKey !== "has_script_breakdown") {
                                    field.onChange(!field.value);
                                    return;
                                  }
                                  if (!field.value) {
                                    field.onChange(true);
                                    return;
                                  }
                                  const { has_budget: b, has_schedule: s } = getValues("features");
                                  if (b || s) {
                                    return;
                                  }
                                  field.onChange(false);
                                }}
                                checkboxColor={CheckboxColor.Yellow}
                                className="mt-1"
                              />
                            )}
                          />
                        </div>
                        {featureKey === "has_script_breakdown" && finalDraftTagCount > 0 ? (
                          <p
                            className="text-xs text-muted-foreground"
                            data-testid="final-draft-breakdown-helper"
                          >
                            {hasScriptBreakdown
                              ? FINAL_DRAFT_BREAKDOWN_ON_HELPER
                              : FINAL_DRAFT_BREAKDOWN_OFF_HELPER}
                          </p>
                        ) : null}
                        {featureKey === "has_script_breakdown" &&
                          typeof scriptBreakdownMessage === "string" && (
                            <p className="text-xs text-danger pl-0">{scriptBreakdownMessage}</p>
                          )}
                      </div>
                    );
                  })}

                  {group.id === "budget" && (
                    <div className="border-t border-border pt-3">
                      <Controller
                        name="features.max_budget"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <FormattedNumberInput
                            label="Maximum Budget"
                            name={field.name}
                            value={field.value ?? ""}
                            getInputRef={field.ref}
                            onBlur={field.onBlur}
                            placeholder="Enter maximum budget"
                            max={MAX_BUDGET_VALUE}
                            min={MIN_BUDGET_VALUE}
                            error={!!error}
                            errorMessage={error?.message}
                            disabled={otpDraftWithoutAvailableLicense}
                            decimalScale={0}
                            inputMode="numeric"
                            onValueChange={({ floatValue }) => field.onChange(floatValue)}
                          />
                        )}
                      />
                    </div>
                  )}

                  {group.id === "schedule" && (
                    <div className="border-t border-border pt-3">
                      <Controller
                        name="features.schedule_start_date"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <InputV3
                            {...field}
                            label="Schedule Start Date"
                            type="date"
                            error={!!error}
                            errorMessage={error?.message}
                            disabled={otpDraftWithoutAvailableLicense}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
